import React from 'react';
import { motion } from 'framer-motion';
import clsx from 'clsx';
import { PathologyLab } from '../../types';
import { RevenueIcon } from '../icons/RevenueIcon';

interface LabCardProps {
  lab: PathologyLab;
  onClick?: () => void;
  variant?: 'default' | 'selected' | 'excluded' | 'target';
}

export const LabCard: React.FC<LabCardProps> = ({
  lab,
  onClick,
  variant = 'default',
}) => {
  const borderColor = {
    default: 'border-medium-gray',
    selected: 'border-forest-green',
    excluded: 'border-medium-gray opacity-40',
    target: 'border-forest-green border-l-4',
  }[variant];

  const bgColor = variant === 'excluded' ? 'bg-slate-blue opacity-50' : 'bg-slate-blue';

  return (
    <motion.div
      className={clsx(
        'rounded-lg border-2 p-4 cursor-pointer transition-all',
        'shadow-md hover:shadow-lg',
        borderColor,
        bgColor
      )}
      whileHover={{ y: -4, scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      onClick={onClick}
      layout
    >
      {/* Investment Strategy Badge */}
      {lab.labType && (
        <div className="mb-2">
          {lab.labType === 'platform' ? (
            <span className="inline-block bg-forest-green text-white text-xs font-bold px-3 py-1 rounded-full">
              🎯 PLATFORM CANDIDATE
            </span>
          ) : (
            <span className="inline-block bg-light-blue text-white text-xs font-bold px-3 py-1 rounded-full">
              🔧 ADD-ON CANDIDATE
            </span>
          )}
        </div>
      )}

      {/* Header */}
      <h3 className="font-bold text-white text-base mb-2">
        {lab.name}
      </h3>

      {/* Details */}
      <div className="space-y-1 text-sm">
        <div className="text-light-gray">
          <span className="text-light-gray opacity-70">Region:</span> {lab.location.region}
        </div>

        <div className="text-forest-green font-bold flex items-center gap-1">
          <RevenueIcon size={16} /> ${lab.revenue}M Revenue
        </div>

        <div className="text-light-gray">
          <span className="text-light-gray opacity-70">Locations:</span>{' '}
          {lab.characteristics.locationsCount}
        </div>

        {lab.characteristics.ownerAge && (
          <div className="text-light-gray">
            <span className="text-light-gray opacity-70">Owner Age:</span>{' '}
            {lab.characteristics.ownerAge}
          </div>
        )}

        <div className="text-light-gray">
          <span className="text-light-gray opacity-70">Tech:</span>{' '}
          {lab.characteristics.techInfrastructure}
        </div>

        {variant === 'target' && (
          <div className="mt-2 pt-2 border-t border-light-gray">
            <div className="text-forest-green font-bold text-sm">
              ⭐ Platform Score: {lab.scores.platformFit}
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
};
